/**
 * seo/image-alt — 이미지 대체문구(alt) 규칙 한 곳(P3).
 *
 * 공식: 「{현장명} {이미지 종류} — {시군구} {상황 키워드}」
 *   · 현장명 = 사람들이 실제 검색하는 이름(apt_sites.display_name 앞 토막 → 검색 별칭 → name 순 · searchSiteName).
 *   · 이미지 종류 = KIND_LABEL. AI 생성 그림은 「AI 상상도」 — 공식 이미지 이름과 섞지 않는다(저장소 규칙 HC-E-01).
 *   · 상황 키워드 = lifecycle_stage → 사람들이 검색하는 말(분양예정·청약 접수·입주예정 …). 모르는 단계는 생략.
 *   · 재료가 비면 그 조각만 빠진다(빈 값·자리표시 0).
 *
 * 상세 갤러리처럼 이미 cl-core/alt 공식이 있는 자리는 siteImageAlt 가 그 공식에 시군구·상황 꼬리만 덧붙인다(공식 하나를 두 벌로 만들지 않는다).
 * 장식용 아이콘은 이 규칙을 쓰지 않는다 — alt="" 그대로.
 * 순수 TS(서버·클라이언트 공용 · 조회 0).
 */
import { altFor, type AltInput, type ClImageKind } from '@/lib/cl-core/alt';
import { LIFECYCLE_LABEL, PIPELINE_STAGES } from '@/lib/apt/lifecycle-label';

export type ImageAltKind =
  | 'official' // 시행·시공사 제공(종류 불명 — 단정하지 않는다)
  | 'birdseye' // 공식 자료로 확인된 조감도만
  | 'ai_render'
  | 'site_plan'
  | 'satellite'
  | 'model_house'
  | 'floor_plan'
  | 'photo'
  | 'cover'
  | 'info_card'
  | 'chart'
  | 'related';

const KIND_LABEL: Record<ImageAltKind, string> = {
  official: '공식 이미지',
  birdseye: '조감도',
  ai_render: 'AI 상상도',
  site_plan: '단지 배치도',
  satellite: '위성 사진',
  model_house: '모델하우스',
  floor_plan: '평면도',
  photo: '단지 사진',
  cover: '대표 이미지',
  info_card: '단지 정보 카드',
  chart: '정보 그래픽',
  related: '관련 이미지',
};

export const IMAGE_KIND_LABEL: Readonly<Record<ImageAltKind, string>> = KIND_LABEL;

/** lifecycle_stage → 상황 키워드(검색어 꼴). 모르는 값·빈 값은 null. */
export function situationKeyword(stage: string | null | undefined, opts?: { moveInPast?: boolean }): string | null {
  const s = String(stage ?? '').trim();
  if (!s) return null;
  if ((PIPELINE_STAGES as readonly string[]).includes(s)) return `${LIFECYCLE_LABEL[s]} 분양예정`;
  switch (s) {
    case 'site_planning':
    case 'pre_announcement': return '분양예정';
    case 'model_house_open': return '모델하우스 오픈';
    case 'special_supply': return '특별공급 청약';
    case 'subscription_open': return '청약 접수';
    case 'award_pending': return '청약 당첨자 발표 예정';
    case 'award_announced': return '당첨자 발표';
    case 'contract':
    case 'contract_signing': return '정당계약';
    case 'unsold_active': return '잔여세대 분양';
    case 'pre_move_in':
    case 'move_in_ready': return opts?.moveInPast ? '입주' : '입주예정';
    case 'move_in':
    case 'move_in_started': return '입주';
    case 'post_move_in':
    case 'active_trade':
    case 'resale': return '실거래 시세';
    case 'redevelopment_active': return '재개발';
    default: return null;
  }
}

/** 검색되는 현장 이름 — display_name 앞 토막(「 — 」 앞) → 첫 검색 별칭 → name. */
export function searchSiteName(row: { display_name?: string | null; name?: string | null; aliases?: ReadonlyArray<string | null | undefined> | null }): string {
  const d = String(row?.display_name ?? '').split(' — ')[0].trim();
  if (d) return d;
  const a = (row?.aliases ?? []).map((x) => String(x ?? '').trim()).find(Boolean);
  return a || String(row?.name ?? '').trim();
}

const MEANINGLESS = /^(?:이미지|image|img|사진|그림|photo|picture|pic|thumbnail|thumb|썸네일|대표\s*이미지|첨부\s*이미지|null|undefined|none|alt|untitled|\d+)$/i;

/** 비었거나 무의미한 alt(「이미지」「image」·파일 이름·숫자만) 인가. */
export function isMeaninglessAlt(alt: string | null | undefined): boolean {
  const s = String(alt ?? '').replace(/\s+/g, ' ').trim();
  if (s.length < 2) return true;
  if (MEANINGLESS.test(s)) return true;
  if (/^[\w.-]+\.(?:jpe?g|png|webp|gif|avif|svg)$/i.test(s)) return true;
  if (/^(?:이미지|image|사진)\s*\d+$/i.test(s)) return true;
  return false;
}

function clean(v: string | null | undefined): string {
  return String(v ?? '').replace(/\s+/g, ' ').trim();
}

export interface ImageAltInput {
  name: string;
  kind: ImageAltKind;
  /** 시군구(없으면 region). */
  sigungu?: string | null;
  region?: string | null;
  /** 상황 키워드(situationKeyword 결과) — stage 보다 우선. */
  situation?: string | null;
  stage?: string | null;
  /** 종류 라벨 대신 쓸 말(예: 「단지 외관 AI 상상도」). */
  label?: string | null;
  /** AI 생성 그림이면 true — 종류를 AI 상상도로 고정한다. */
  ai?: boolean;
}

function kindLabelOf(kind: ImageAltKind, label: string | null | undefined, ai: boolean): string {
  let l = clean(label) || KIND_LABEL[kind];
  // AI 그림은 공식 이미지 이름을 쓰지 않는다(HC-E-01 · cl-core captionFor 와 같은 치환).
  if (ai || kind === 'ai_render') {
    l = l.replace(new RegExp(KIND_LABEL.birdseye, 'g'), '상상도');
    if (!/상상도/.test(l)) l = `${l} ${KIND_LABEL.ai_render}`;
  }
  return l;
}

/** 공식 alt — 「{현장명} {종류} — {시군구} {상황}」(최대 200자 · 항상 비지 않음). */
export function imageAlt(i: ImageAltInput): string {
  const name = clean(i.name);
  const ai = !!i.ai || i.kind === 'ai_render';
  const kind: ImageAltKind = ai && (i.kind === 'official' || i.kind === 'birdseye') ? 'ai_render' : i.kind;
  const label = kindLabelOf(kind, i.label, ai);
  const area = clean(i.sigungu) || clean(i.region);
  const sit = clean(i.situation) || clean(situationKeyword(i.stage ?? null));
  const head = name ? `${name} ${label}` : label;
  const tail = [area && !name.includes(area) ? area : '', sit && !head.includes(sit) ? sit : ''].filter(Boolean).join(' ');
  return (tail ? `${head} — ${tail}` : head).slice(0, 200);
}

/** 이미 있는 alt 에 시군구·상황 꼬리를 덧붙인다(이미 들어 있으면 그대로). */
export function withAltTail(alt: string, parts: { sigungu?: string | null; situation?: string | null }): string {
  let out = clean(alt);
  for (const p of [clean(parts.sigungu), clean(parts.situation)]) {
    if (!p || out.includes(p)) continue;
    out = out.includes(' — ') ? `${out} ${p}` : `${out} — ${p}`;
  }
  return out.slice(0, 200);
}

/**
 * 상세 갤러리 alt — cl-core altFor 공식 + 시군구·상황 꼬리.
 * 단계 카드(이미 「현재 {단계}」)·지역 카드(현장 한 곳 그림이 아님)는 공식 그대로.
 */
export function siteImageAlt(kind: ClImageKind, base: AltInput, situation: string | null | undefined, given?: string | null): string {
  const alt = clean(given) || altFor(kind, base);
  if (kind === 'stage_card' || kind === 'region_card' || kind === 'region_collage') return alt;
  return withAltTail(alt, { sigungu: base.sigungu ?? null, situation });
}

/** 주소·class 로 이미지 종류 추정(본문 HTML 용). 모르면 null. */
export function inferImageKind(src: string, attrs = ''): ImageAltKind | null {
  const s = `${src} ${attrs}`.toLowerCase();
  if (/satellite|vworld|\/sat[/_-]/.test(s)) return 'satellite';
  if (/data-ai|ai[_-]render|ai[_-]thumb|\/img\/[^/]+\/s\d|\/vx\//.test(s)) return 'ai_render';
  if (/floor[_-]?plan|평면/.test(s)) return 'floor_plan';
  if (/site[_-]?plan|layout[_-]plan|배치/.test(s)) return 'site_plan';
  if (/model[_-]?house|모델하우스|견본/.test(s)) return 'model_house';
  if (/\/api\/og|og[_-]card|chart|infographic|_card\b/.test(s)) return 'chart';
  return null;
}

export interface HtmlAltContext {
  /** 글이 다루는 현장의 검색 이름(없으면 null — 글 제목으로 대신한다). */
  siteName?: string | null;
  sigungu?: string | null;
  situation?: string | null;
  /** 현장이 없을 때 쓸 이름(글 제목). */
  title: string;
  /** 현장이 없을 때 종류 라벨 앞에 붙일 분류(예: 「부동산」). */
  catLabel?: string | null;
}

function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function unescAttr(s: string): string {
  return s.replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
}

function attrOf(tag: string, name: string): string | null {
  const m = new RegExp(`\\s${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, 'i').exec(tag);
  return m ? unescAttr(m[1] ?? m[2] ?? '') : null;
}

function altForHtml(src: string, tag: string, ctx: HtmlAltContext): string {
  const kind = inferImageKind(src, tag);
  const site = clean(ctx.siteName);
  if (site) return imageAlt({ name: site, kind: kind ?? 'related', sigungu: ctx.sigungu, situation: ctx.situation });
  const cat = clean(ctx.catLabel);
  return imageAlt({ name: clean(ctx.title), kind: kind ?? 'related', label: kind ? null : cat ? `${cat} 관련 이미지` : null, sigungu: ctx.sigungu });
}

/** 현장 없이 제목만으로 만든 alt(본문 파이프라인 1차 채움)와 같은가 — 렌더 시점에 현장 이름으로 다시 채울 대상. */
function isTitleFallback(alt: string, src: string, tag: string, ctx: HtmlAltContext): boolean {
  if (!clean(ctx.siteName)) return false;
  return alt === altForHtml(src, tag, { title: ctx.title, catLabel: ctx.catLabel, sigungu: ctx.sigungu }) ||
    alt === altForHtml(src, tag, { title: ctx.title, catLabel: ctx.catLabel });
}

/**
 * 본문 HTML 의 <img> 중 alt 가 없거나 무의미한 것만 규칙으로 채운다(렌더 시점 · DB 원문 불변).
 * 같은 alt 가 겹치면 뒤에 「 2」「 3」을 붙여 고유하게 한다. 정상 alt·다른 속성·나머지 바이트는 그대로.
 */
export function fillHtmlImageAlts(html: string, ctx: HtmlAltContext): string {
  if (!html || html.indexOf('<img') < 0) return html;
  const used = new Map<string, number>();
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    const src = attrOf(tag, 'src') ?? '';
    const cur = attrOf(tag, 'alt');
    if (cur != null && !isMeaninglessAlt(cur) && !isTitleFallback(cur, src, tag, ctx)) {
      used.set(cur, (used.get(cur) ?? 0) + 1);
      return tag;
    }
    let alt = altForHtml(src, tag, ctx);
    const n = (used.get(alt) ?? 0) + 1;
    used.set(alt, n);
    if (n > 1) alt = `${alt} ${n}`;
    const v = escAttr(alt);
    if (cur == null) return tag.replace(/^<img\b/i, `<img alt="${v}"`);
    return tag.replace(/\salt\s*=\s*(?:"[^"]*"|'[^']*')/i, ` alt="${v}"`);
  });
}
